import { decorate, observable, action, runInAction } from 'mobx';
import { ParentStore } from '@mq/voltron-parent';
import AddressStore from './AddressStore';
import IdentificationStore from './IdentificationStore';

class BusinessDirectorStore extends ParentStore {
  constructor(args: Object = {}) {
    super(args);
    this.load(args);
  }
  // values
  token: string = '';
  active: boolean = false;
  first_name: string = '';
  middle_name: string = '';
  last_name: string = '';
  email: string = '';
  phone: string = '';
  birth_date: string = '';
  business_token: string = '';
  title: string = '';
  nationality: string = '';
  place_of_birth: string = '';
  created_time: string = '';
  last_modified_time: string = '';
  loading: boolean = false;
  identificationType: string = '';
  identificationValue: string = '';
  canEditIdentification: boolean = false;
  identificationHasBeenUpdated: boolean = false;

  // objects
  address: AddressStore = {};
  identifications: [IdentificationStore] = [];

  // actions
  loadAndConstruct(args) {
    this.load(args);
    this.loadAndConstructItem('address', args.address, AddressStore);
  }

  async getDirectorData(businessToken) {
    try {
      const result = await this.gqlQuery(
        `
          query director($business_token: String!, $director_token: String!) {
            director(business_token: $business_token, director_token: $director_token) {
              token
              active
              first_name
              middle_name
              last_name
              email
              phone
              birth_date
              title
              nationality
              place_of_birth
              address {
                address1
                address2
                city
                state
                country
                postal_code
                zip
              }
            }
          }
        `,
        {
          business_token: businessToken,
          director_token: this.token,
        }
      );

      return runInAction(() => {
        if (result) {
          const directorData = this.extract(result, 'director');
          this.business_token = businessToken;
          this.loadAndConstruct(directorData);
          return true;
        } else {
          return false;
        }
      });
    } catch (error) {
      console.error('Error fetching director data:', error);
    }
  }

  async getDirectorIdentifications(businessToken) {
    const result = await this.gqlQuery(
      `
        query directorIdentifications($business_token: String!, $director_token: String!, $full_ssn: Boolean) {
          directorIdentifications(business_token: $business_token, director_token: $director_token, full_ssn: $full_ssn) {
            nin
            ssn
            sin
            tin
          }
        }
      `,
      {
        business_token: businessToken,
        director_token: this.token,
        full_ssn: true,
      }
    );

    return runInAction(() => {
      if (result) {
        const directorIdentificationsRaw = this.extract(result, 'directorIdentifications');

        let identificationObj = {};
        const identificationTypes = ['nin', 'ssn', 'sin', 'tin'];

        Object.entries(directorIdentificationsRaw)
          .filter(([, value]) => value !== null) // Filter out entries where the value is null
          .forEach(([key, value]) => {
            if (identificationTypes.includes(key)) {
              identificationObj.type = key;
              identificationObj.value = value;
            } // Add the remaining key-value pairs to the new object
          });
        this.loadAndConstructList('identifications', [identificationObj], IdentificationStore);
        this.identificationType = identificationObj.type || null;
        this.identificationValue = identificationObj.value || null;
        return true;
      } else {
        return false;
      }
    });
  }

  constructAddressObjForUpdateDirectorPayload() {
    return {
      address1: this.address.address1,
      address2: this.address.address2,
      city: this.address.city,
      state: this.address.state,
      country: this.address.country,
      postal_code: this.address.postal_code,
      zip: this.address.zip,
    };
  }

  constructIdentificationObjForUpdateDirectorPayload() {
    return [
      {
        type: this.identificationType,
        value: this.identificationValue,
      },
    ];
  }

  async updateDirector() {
    const paramsInfo = {
      business_token: { type: 'String!', val: this.business_token },
      token: { type: 'String', val: this.token },
      first_name: { type: 'String', val: this.first_name },
      middle_name: { type: 'String', val: this.middle_name },
      last_name: { type: 'String', val: this.last_name },
      title: { type: 'String', val: this.title },
      address: { type: 'AddressInput', val: this.constructAddressObjForUpdateDirectorPayload() },
      email: { type: 'String', val: this.email },
      phone: { type: 'String', val: this.phone },
      birth_date: { type: 'String', val: this.birth_date },
      nationality: { type: 'String', val: this.nationality },
      place_of_birth: { type: 'String', val: this.place_of_birth },
    };

    if (this.identificationHasBeenUpdated) {
      paramsInfo.identifications = {
        type: '[IdentificationInput]',
        val: this.constructIdentificationObjForUpdateDirectorPayload(),
      };
    }

    const result = await this.gqlMutation(
      `
      mutation updateBusinessDirector(${this.configureOuterQueryParams(paramsInfo)}) {
        updateBusinessDirector(${this.configureInnerQueryParams(paramsInfo)}) {
          token
          active
          first_name
          middle_name
          last_name
          email
          phone
          birth_date
          title
          nationality
          place_of_birth
          address {
            address1
            address2
            city
            state
            country
            postal_code
            zip
          }
        }
      }
    `,
      this.configureQueryParams(paramsInfo)
    );

    return runInAction(() => {
      if (result) {
        const updatedDirectorData = this.extract(result, 'updateBusinessDirector');
        this.loadAndConstruct(updatedDirectorData);
        this.identificationHasBeenUpdated = false;
        return true;
      } else {
        console.error('Failed to update director');
        return false;
      }
    });
  }
}

decorate(BusinessDirectorStore, {
  //values
  token: observable,
  active: observable,
  first_name: observable,
  business_token: observable,
  middle_name: observable,
  last_name: observable,
  email: observable,
  phone: observable,
  birth_date: observable,
  title: observable,
  nationality: observable,
  place_of_birth: observable,
  created_time: observable,
  last_modified_time: observable,
  loading: observable,
  identificationType: observable,
  identificationValue: observable,
  identificationHasBeenUpdated: observable,

  // objects
  address: observable,
  identifications: observable,

  //actions
  loadAndConstruct: action.bound,
  getDirectorData: action.bound,
  getDirectorIdentifications: action.bound,
  updateDirector: action.bound,
});

export default BusinessDirectorStore;
